import { ICONS } from 'src/utils/constants';

export interface AuditActivityMetadata {
    label: string;
    icon: string;
}

const LABEL_OVERRIDES: { [eventType: string]: string } = {
    ADS_UPDATED: 'Updated automated dispense system',
    AI_PROMPT_CREATED: 'Created AI prompt',
    AI_PROMPT_UPDATED: 'Updated AI prompt',
    AI_PROMPT_CONTEXT_CREATED: 'Created AI prompt context',
    AI_PROMPT_CONTEXT_UPDATED: 'Updated AI prompt context',
    AI_PROMPT_CONTEXT_DEACTIVATED: 'Deactivated AI prompt context',
    AI_PROMPT_CONTEXT_ACTIVATED: 'Activated AI prompt context',
    API_KEY_CREATED: 'Created API key',
    API_KEY_REVOKED: 'Revoked API key',
    ENCTR_CREATED: 'Created encounter',
    ENCTR_UPDATED: 'Updated encounter',
    ENCTR_ACCESSED: 'Opened encounter',
    ENCTR_SEARCH_PERFORMED: 'Searched encounters',
    ENCTR_FORM_CREATED: 'Created encounter form',
    ENCTR_FORM_UPDATED: 'Updated encounter form',
    ENCTR_FORM_ACCESSED: 'Opened encounter form',
    ENCTR_FORM_VOIDED: 'Voided encounter form',
    ENCTR_FORM_UNVOIDED: 'Unvoided encounter form',
    ENCTR_FORM_OPENED: 'Opened encounter form',
    ENCTR_FORM_MODEL_UPDATED: 'Updated form',
    ENCOUNTERS_SEARCHED: 'Searched encounters',
    ENCOUNTERS_SEARCHED_BY_DATE: 'Searched encounters by date',
    ENCOUNTER_FORM_VOIDED: 'Voided encounter form',
    FRAMEWORK_SURVEY_CREATED: 'Created survey',
    FRAMEWORK_SURVEY_STATE_CHANGE: 'Changed survey state',
    FRAMEWORK_SURVEY_REQUEST_STATE_CHANGE: 'Changed survey request state',
    FRAMEWORK_SURVEY_REQUEST_UNSUBSCRIBED: 'Unsubscribed survey request',
    FRAMEWORK_SURVEY_COMPLETED: 'Completed survey',
    FRAMEWORK_SURVEY_REQUEST_CREATED: 'Created survey request',
    MOBILE_LOGIN: 'Logged in from mobile',
    MOBILE_SYNC_PERFORMED: 'Performed mobile sync',
    PATIENT_MRN_VIEWED: 'Viewed patient MRN',
    PATIENT_SSN_VIEWED: 'Viewed patient SSN',
    USER_LOGGED_IN: 'Logged in',
    USER_LOGIN_FAILED: 'Failed login',
    USER_SELECTED_ORG: 'Selected organization',
    USER_GRANT_FACILITY: 'Granted facility access',
    USER_DEACTIVATE_FACILITY: 'Deactivated facility access',
    USER_REACTIVATE_FACILITY: 'Reactivated facility access',
    USER_PREFERENCES_ACCESSED: 'Opened user preferences',
    USER_PREFERENCE_ACCESSED: 'Opened user preference',
    VALSET_DETAIL_CREATED: 'Created value set detail',
    VALSET_DETAIL_UPDATED: 'Updated value set detail',
    VALSET_DETAIL_DEACTIVATED: 'Deactivated value set detail',
    VALSET_DETAIL_REACTIVATED: 'Reactivated value set detail',
};

function getFriendlyWords(eventType: string): string[] {
    return eventType
        .toLowerCase()
        .split('_')
        .map((word) => {
            switch (word) {
                case 'api':
                    return 'API';
                case 'ai':
                    return 'AI';
                case 'enctr':
                    return 'Encounter';
                case 'mrn':
                    return 'MRN';
                case 'npi':
                    return 'NPI';
                case 'ssn':
                    return 'SSN';
                case 'valset':
                    return 'Value set';
                default:
                    return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
            }
        });
}

function getSubjectLabel(words: string[]) {
    return words.map((word) => (word === word.toUpperCase() ? word : word.toLowerCase())).join(' ');
}

function getFriendlyLabel(eventType: string): string {
    if (LABEL_OVERRIDES[eventType]) {
        return LABEL_OVERRIDES[eventType];
    }

    const words = getFriendlyWords(eventType);
    const action = words[words.length - 1];
    const actionLabelByEventSuffix: { [action: string]: string } = {
        Accessed: 'Opened',
        Activated: 'Activated',
        Added: 'Added',
        Archived: 'Archived',
        Canceled: 'Canceled',
        Cancelled: 'Canceled',
        Changed: 'Changed',
        Completed: 'Completed',
        Created: 'Created',
        Deactivated: 'Deactivated',
        Downloaded: 'Downloaded',
        Ended: 'Ended',
        Inserted: 'Inserted',
        Joined: 'Joined',
        Modified: 'Modified',
        Opened: 'Opened',
        Performed: 'Performed',
        Reactivated: 'Reactivated',
        Removed: 'Removed',
        Revoked: 'Revoked',
        Searched: 'Searched',
        Sent: 'Sent',
        Unarchived: 'Unarchived',
        Unvoided: 'Unvoided',
        Updated: 'Updated',
        Viewed: 'Viewed',
        Voided: 'Voided',
    };

    if (actionLabelByEventSuffix[action] && words.length > 1) {
        return `${actionLabelByEventSuffix[action]} ${getSubjectLabel(words.slice(0, -1))}`;
    }

    return words.join(' ');
}

function mapEventTypes(icon: string, eventTypes: string[]) {
    return eventTypes.reduce<{ [eventType: string]: AuditActivityMetadata }>((metadata, eventType) => {
        metadata[eventType] = {
            label: getFriendlyLabel(eventType),
            icon,
        };
        return metadata;
    }, {});
}

export const AUDIT_ACTIVITY_METADATA: { [eventType: string]: AuditActivityMetadata } = {
    ...mapEventTypes(ICONS.TimeClockCircle1Alternate, [
        'USER_LOGGED_IN',
        'USER_LOGIN_FAILED',
        'USER_SELECTED_ORG',
        'MOBILE_LOGIN',
        'MOBILE_SYNC_PERFORMED',
    ]),
    ...mapEventTypes(ICONS.MultipleUsersAdd, [
        'INVITE_CREATED',
        'INVITE_RESENT',
        'INVITE_ACCEPTED',
        'INVITE_CANCELED',
        'USER_PASSWORD_RESET_REQUESTED',
        'USER_PASSWORD_RESET',
        'USER_CREATED',
        'USER_UPDATED',
        'USER_DEACTIVATED',
        'USER_REACTIVATED',
        'USER_GRANT_FACILITY',
        'USER_DEACTIVATE_FACILITY',
        'USER_REACTIVATE_FACILITY',
        'USER_PREFERENCE_CREATED',
        'USER_PREFERENCE_UPDATED',
        'USER_PREFERENCES_ACCESSED',
        'USER_PREFERENCE_ACCESSED',
    ]),
    ...mapEventTypes(ICONS.PatientVisit, [
        'PATIENT_CREATED',
        'PATIENT_STATUS_CHANGED',
        'PATIENT_UPDATED',
        'PATIENT_ACCESSED',
        'PATIENT_SEARCH_PERFORMED',
        'PATIENT_NEED_CREATED',
        'PATIENT_NEED_UPDATED',
        'PATIENT_NEED_STATUS_CHANGED',
        'PATIENT_ATTACHMENT_CREATED',
        'PATIENT_ATTACHMENT_UPDATED',
        'PATIENT_ATTACHMENT_REMOVED',
        'PATIENT_ATTACHMENT_ACCESSED',
        'PATIENT_NOTE_CREATED',
        'PATIENT_NOTE_UPDATED',
        'PATIENT_NOTE_REMOVED',
        'PATIENT_MEDICATION_CREATED',
        'PATIENT_MEDICATION_UPDATED',
        'PATIENT_MRN_VIEWED',
        'PATIENT_SSN_VIEWED',
    ]),
    ...mapEventTypes(ICONS.Appointment, [
        'APPOINTMENT_CREATED',
        'APPOINTMENT_UPDATED',
        'APPOINTMENT_STATUS_CHANGED',
        'APPOINTMENT_ACCESSED',
    ]),
    ...mapEventTypes(ICONS.Task, ['TASK_CREATED', 'TASK_UPDATED', 'TASK_STATUS_CHANGED', 'TASK_ACCESSED']),
    ...mapEventTypes(ICONS.Visit, [
        'ENCTR_CREATED',
        'ENCTR_UPDATED',
        'ENCTR_ACCESSED',
        'ENCTR_SEARCH_PERFORMED',
        'ENCOUNTERS_SEARCHED',
        'ENCOUNTERS_SEARCHED_BY_DATE',
        'ENCTR_FORM_SEARCH_PERFORMED',
        'ENCTR_FORM_CREATED',
        'ENCTR_FORM_UPDATED',
        'ENCTR_FORM_ACCESSED',
        'ENCTR_FORM_VOIDED',
        'ENCTR_FORM_UNVOIDED',
        'ENCTR_FORM_MODEL_UPDATED',
        'ENCOUNTER_FORM_VOIDED',
        'ENCTR_FORM_OPENED',
        'STROKE_ADDED',
        'PRINT_FORM',
    ]),
    ...mapEventTypes(ICONS.Attachment, [
        'DOCUMENT_SCAN_CREATED',
        'DOCUMENT_SCAN_UPDATED',
        'DOCUMENT_SCAN_ACCESSED',
        'DOCUMENT_SCAN_ARCHIVED',
        'DOCUMENT_SCAN_UNARCHIVED',
        'DOCUMENT_SCAN_ENCOUNTER_CREATED',
        'DOCUMENT_SCAN_ASSET_CREATED',
        'DOCUMENT_SCAN_ASSET_UPDATED',
        'DOCUMENT_SCAN_ENTITY_CREATED',
    ]),
    ...mapEventTypes(ICONS.LaptopHeart, [
        'TELEHEALTH_SESSION_CREATED',
        'TELEHEALTH_SESSION_UPDATED',
        'TELEHEALTH_SESSION_OPENED',
        'TELEHEALTH_SESSION_RECORDING_VIEWED',
        'TELEHEALTH_SESSION_RECORDING_DOWNLOADED',
        'TELEHEALTH_SESSION_COMPLETED',
        'TELEHEALTH_SESSION_PATIENT_AUTHENTICATE_ATTEMPT',
        'TELEHEALTH_SESSION_CANCELLED',
        'TELEHEALTH_CALL_JOINED',
        'TELEHEALTH_CALL_ENDED',
        'TELEHEALTH_INVITE_SENT',
    ]),
    ...mapEventTypes(ICONS.Cog3, [
        'REFERENCE_LIST_CREATED',
        'REFERENCE_LIST_UPDATED',
        'REFERENCE_LIST_DEACTIVATED',
        'REFERENCE_LIST_REACTIVATED',
        'REFERENCE_LIST_VALUE_CREATED',
        'REFERENCE_LIST_VALUE_UPDATED',
        'REFERENCE_LIST_VALUE_DEACTIVATED',
        'REFERENCE_LIST_VALUE_REACTIVATED',
        'VALSET_DETAIL_CREATED',
        'VALSET_DETAIL_UPDATED',
        'VALSET_DETAIL_DEACTIVATED',
        'VALSET_DETAIL_REACTIVATED',
        'TAG_CREATED',
        'TAG_UPDATED',
        'FACILITY_UPDATED',
        'FACILITY_DEACTIVATED',
        'FACILITY_REACTIVATED',
        'VITALS_CONNECTION_CREATED',
        'VITALS_CONNECTION_UPDATED',
        'VITALS_CONNECTION_DEACTIVATED',
        'VITALS_CONNECTION_REACTIVATED',
        'ADS_UPDATED',
        'AI_PROMPT_CREATED',
        'AI_PROMPT_UPDATED',
        'AI_PROMPT_CONTEXT_CREATED',
        'AI_PROMPT_CONTEXT_UPDATED',
        'AI_PROMPT_CONTEXT_DEACTIVATED',
        'AI_PROMPT_CONTEXT_ACTIVATED',
        'API_KEY_CREATED',
        'API_KEY_REVOKED',
    ]),
    ...mapEventTypes(ICONS.NavigationPayment, [
        'PAYER_CATEGORY_CREATED',
        'PAYER_CATEGORY_UPDATED',
        'PAYER_CATEGORY_REACTIVATED',
        'PAYER_CATEGORY_DEACTIVATED',
        'PAYER_CREATED',
        'PAYER_UPDATED',
        'PAYMENT_TYPE_CREATED',
        'PAYMENT_TYPE_UPDATED',
        'PAYMENT_TYPE_REACTIVATED',
        'PAYMENT_TYPE_DEACTIVATED',
        'PAYMENT_GATEWAY_CREATED',
        'PAYMENT_GATEWAY_UPDATED',
        'PAYMENT_GATEWAY_REACTIVATED',
        'PAYMENT_GATEWAY_DEACTIVATED',
        'PAYMENT_REQUEST_CREATED',
        'PAYMENT_REQUEST_UPDATED',
        'PAYMENT_REQUEST_STATE_CHANGED',
        'PAYMENT_REQUEST_CONTACT_CREATED',
        'PAYMENT_REQUEST_CONTACT_UPDATED',
        'PAYMENT_REQUEST_CONTACT_STATE_CHANGED',
        'PAYMENT_TRANSACTION_CREATED',
        'PAYMENT_TRANSACTION_UPDATED',
        'PAYMENT_TRANSACTION_STATE_CHANGED',
    ]),
    ...mapEventTypes(ICONS.Upload, [
        'IMPORT_BATCH_CREATED',
        'FRAMEWORK_SURVEY_CREATED',
        'FRAMEWORK_SURVEY_STATE_CHANGE',
        'FRAMEWORK_SURVEY_REQUEST_STATE_CHANGE',
        'FRAMEWORK_SURVEY_REQUEST_UNSUBSCRIBED',
        'FRAMEWORK_SURVEY_COMPLETED',
        'FRAMEWORK_SURVEY_REQUEST_CREATED',
        'SURVEY_TEMPLATE_CREATED',
        'SURVEY_TEMPLATE_UPDATED',
        'SURVEY_DEFINITION_QUESTION_INSERTED',
        'SURVEY_DEFINITION_QUESTION_ARCHIVED',
        'SURVEY_DEFINITION_QUESTION_UNARCHIVED',
        'SURVEY_DEFINITION_QUESTION_MODIFIED',
        'SURVEY_TEMPLATE_QUESTION_UNARCHIVED',
        'SURVEY_TEMPLATE_QUESTION_ARCHIVED',
        'SURVEY_TEMPLATE_QUESTION_CREATED',
        'SURVEY_TEMPLATE_QUESTION_UPDATED',
    ]),
    ...mapEventTypes(ICONS.SingleNeutralActionsAddress, [
        'CUSTOMER_UPDATED',
        'CUSTOMER_CREATED',
        'CUSTOMER_STATUS_UPDATED',
        'CUSTOMER_CONTACT_CREATED',
        'CUSTOMER_CONTACT_UPDATED',
        'CUSTOMER_CONTACT_REMOVED',
    ]),
};

export function getAuditActivityMetadata(eventType?: string | null): AuditActivityMetadata {
    if (!eventType) {
        return {
            label: 'Activity',
            icon: ICONS.Details,
        };
    }

    return (
        AUDIT_ACTIVITY_METADATA[eventType] || {
            label: getFriendlyLabel(eventType),
            icon: ICONS.Details,
        }
    );
}
